import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EMBROIDERY_TYPES } from "../../constants/imageGeneratorDictionaries";
import {
  ExclusivePills,
  FieldLabel,
  chipClassName,
} from "./ImageGeneratorControls";

const isListedEmbroidery = (value: string) =>
  (EMBROIDERY_TYPES as readonly string[]).includes(value);

type Props = {
  value: string;
  onChange: (value: string) => void;
  showTitle?: boolean;
};

const ImageGeneratorEmbroideryType: React.FC<Props> = ({
  value,
  onChange,
  showTitle = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const isCustom = Boolean(value) && !isListedEmbroidery(value);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    setDraft(isCustom ? value : "");
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen, isCustom, value]);

  const submitCustom = () => {
    const next = draft.trim();
    if (!next) {
      return;
    }
    onChange(next);
    setModalOpen(false);
  };

  return (
    <div>
      {showTitle ? (
        <FieldLabel required>Select Embroidery Type</FieldLabel>
      ) : null}
      <ExclusivePills
        options={EMBROIDERY_TYPES.filter(
          emb => emb !== "Describe your own"
        ).map(emb => ({
          value: emb,
          label: emb,
        }))}
        value={isCustom ? "" : value}
        onChange={onChange}
        trailing={
          <button
            type="button"
            title="Describe your own"
            onClick={() => {
              if (isCustom) {
                onChange("");
                return;
              }
              setModalOpen(true);
            }}
            className={chipClassName(isCustom, isListedEmbroidery(value))}
          >
            Describe your own
          </button>
        }
      />

      {modalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[220] flex items-center justify-center bg-[#1F1F1F]/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Describe your own embroidery"
              onClick={() => setModalOpen(false)}
            >
              <div
                className="w-full max-w-[480px] rounded-2xl bg-white p-5 shadow-2xl"
                onClick={event => event.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-base font-bold text-secondary">
                    Describe your own
                  </h2>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-secondary hover:bg-gray"
                    aria-label="Close"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 6l12 12M18 6 6 18"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <label className="mb-4 block">
                  <span className="mb-1.5 block text-sm text-secondary">
                    Embroidery pattern
                  </span>
                  <textarea
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    placeholder="Describe your own embroidery pattern"
                    maxLength={240}
                    rows={4}
                    className="w-full resize-y rounded-xl border border-[#737373] p-3 text-sm text-secondary focus:outline-none"
                    autoFocus
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="h-10 rounded-full border border-[#737373] px-5 text-sm text-secondary hover:bg-gray"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!draft.trim()}
                    onClick={submitCustom}
                    className="h-10 rounded-full bg-black px-8 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Use this
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ImageGeneratorEmbroideryType;
